import * as React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigationContext } from './NavigationContext.tsx';
import classnames from '../../../utils/classnames.tsx';
import { IDLE, PEEK_SMILE } from '../../worki/Pose.tsx';
import Worki, { WorkiHandle } from '../../worki/Worki.tsx';
const MASCOT_SIZE = 64;
const REVEAL_TRANSITION =
    'clip-path 300ms cubic-bezier(0.34, 1.56, 0.64, 1), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)';
const JUMP_DURATION_MS = 700;
const SMILE_LINGER_MS = 500;

export interface WorkiNavButtonProps {
    active?: boolean;
    onSelect?: () => void;
}

// The "Worki" nav entry point: unlike every other nav item, this one shows only the mascot's head clipped
// inside the button at rest, then reveals the full body — poking above the row — on hover/press.
const WorkiNavButton = ({ active = false, onSelect }: WorkiNavButtonProps) => {
    const { t } = useTranslation('assistance');
    const { collapsed } = useNavigationContext();
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const [jumping, setJumping] = useState(false);
    const [smiling, setSmiling] = useState(false);
    const jumpTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const smileTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const mascotAreaRef = useRef<HTMLDivElement>(null);
    const workiRef = useRef<WorkiHandle>(null);

    const revealed = hovered || pressed;
    const handleClick = (e: React.MouseEvent) => {
        setJumping(true);
        setSmiling(true);
        clearTimeout(jumpTimeoutRef.current);
        clearTimeout(smileTimeoutRef.current);
        jumpTimeoutRef.current = setTimeout(() => setJumping(false), JUMP_DURATION_MS);
        smileTimeoutRef.current = setTimeout(() => setSmiling(false), SMILE_LINGER_MS);

        if (!mascotAreaRef.current?.contains(e.target as Node)) {
            workiRef.current?.burst();
        }
    };
    const labelStyle =
        'absolute hover:text-content-hover inset-0 flex items-center truncate transition-[opacity,translate] duration-[250ms] text-content-brand-strong';

    return (
        <li className="flex flex-none flex-col gap-1 rounded-md">
            <span className="flex-none px-4 py-1">
                <button
                    type="button"
                    className={classnames(
                        'relative flex h-9 w-full cursor-pointer items-center rounded-md border-none transition-colors duration-[350ms]',
                        collapsed ? 'justify-center' : 'justify-start pl-3',
                        active
                            ? revealed
                                ? 'bg-surface-brand-medium-hover'
                                : 'bg-surface-brand-medium-default'
                            : 'bg-worki-nav-idle'
                    )}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => {
                        setHovered(false);
                        setPressed(false);
                    }}
                    onMouseDown={() => setPressed(true)}
                    onMouseUp={() => setPressed(false)}
                    onClickCapture={(e) => {
                        onSelect?.();
                        handleClick(e);
                    }}
                >
                    {!collapsed && (
                        <span className="relative z-10 mr-2 h-full min-w-0 flex-1 text-sm font-medium">
                            <span
                                className={classnames(
                                    labelStyle,
                                    revealed ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
                                )}
                            >
                                {t('overview.navigation.chat')}
                            </span>
                            <span
                                className={classnames(
                                    labelStyle,
                                    revealed ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                                )}
                            >
                                {t('overview.navigation.chatHover')}
                            </span>
                        </span>
                    )}
                    <div
                        ref={mascotAreaRef}
                        className="relative flex-none self-end"
                        style={{ width: MASCOT_SIZE, height: MASCOT_SIZE }}
                    >
                        <div
                            className={jumping ? 'animate-worki-nav-jump' : undefined}
                            style={{
                                clipPath: revealed || jumping ? 'none' : 'inset(0 0 22px 0)',
                                transform: jumping
                                    ? undefined
                                    : `translateY(${revealed ? 8 : 11.8}px) scale(${revealed ? 0.88 : 0.55})`,
                                transformOrigin: 'bottom center',
                                transition: jumping ? undefined : REVEAL_TRANSITION,
                            }}
                        >
                            <Worki
                                ref={workiRef}
                                pose={pressed || smiling ? PEEK_SMILE : IDLE}
                                size={MASCOT_SIZE}
                                trackMouse={collapsed ? 'none' : 'element'}
                                showGlow={false}
                            />
                        </div>
                    </div>
                </button>
            </span>
        </li>
    );
};

export default WorkiNavButton;
